import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldIcon, FileTextIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Última actualización: Marzo 2026

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital, incluyendo: nombre, dirección de correo electrónico e información de pago.

2. Uso de la Información
Tu información personal se utiliza exclusivamente para:
• Procesar y completar tu compra.
• Enviarte el acceso al producto digital adquirido.
• Comunicarnos contigo respecto a tu pedido.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartir Información
No vendemos, intercambiamos ni transferimos tu información personal a terceros, excepto a proveedores de servicios de pago necesarios para procesar tu transacción.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies.

6. Tus Derechos
Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento contactándonos directamente.

7. Cambios a Esta Política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Última actualización: Marzo 2026

1. Aceptación de los Términos
Al realizar una compra en nuestro sitio, aceptas estos términos y condiciones en su totalidad.

2. Producto Digital
El Método "Noche en Paz" es un producto digital (ebook en formato PDF) que se entrega de forma inmediata tras confirmar el pago. No es un sustituto de consejo médico profesional.

3. Uso Personal
El contenido adquirido es para uso personal y no puede ser reproducido, distribuido o revendido sin autorización expresa.

4. Propiedad Intelectual
Todo el contenido, incluyendo textos, gráficos y metodologías, está protegido por derechos de autor. Queda prohibida su reproducción total o parcial sin consentimiento previo.

5. Descargo de Responsabilidad
La información proporcionada tiene fines educativos. Los resultados pueden variar según cada bebé y familia. No garantizamos resultados específicos. Consulta siempre con tu pediatra ante cualquier duda médica.

6. Política de Reembolso
Debido a la naturaleza digital del producto y su entrega inmediata, no se aceptan devoluciones una vez completada la descarga.

7. Limitación de Responsabilidad
No nos hacemos responsables de daños directos o indirectos derivados del uso o la imposibilidad de uso del producto.

8. Ley Aplicable
Estos términos se rigen por las leyes aplicables en la jurisdicción correspondiente.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-[#0f172a] border-t border-[#1e293b] py-8" data-id="element-29">
        <div className="max-w-4xl mx-auto px-4" data-id="element-30">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-31">
            <button onClick={() => setActiveModal('privacy')} className="font-['DM_Sans'] text-sm text-gray-400 hover:text-[#d4a853] transition-colors duration-200 flex items-center gap-2" data-id="element-32">
              <ShieldIcon className="w-4 h-4" data-id="element-33" />
              Política de Privacidad
            </button>
            <span className="hidden sm:block text-gray-600" data-id="element-34">•</span>
            <button onClick={() => setActiveModal('terms')} className="font-['DM_Sans'] text-sm text-gray-400 hover:text-[#d4a853] transition-colors duration-200 flex items-center gap-2" data-id="element-35">
              <FileTextIcon className="w-4 h-4" data-id="element-36" />
              Términos y Condiciones
            </button>
          </div>
          <p className="font-['DM_Sans'] text-xs text-gray-600 text-center mt-4" data-id="element-37">
            © {new Date().getFullYear()} Método Noche en Paz. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-38">
        {activeModal && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-39">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-40" />

            {/* Modal content */}
            <motion.div className="relative bg-[#faf7f2] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} data-id="element-41">
              {/* Header */}
              <div className="sticky top-0 bg-[#0f172a] px-6 py-4 flex items-center justify-between" data-id="element-42">
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-white flex items-center gap-2" data-id="element-43">
                  {activeModal === 'privacy' ? <>
                      <ShieldIcon className="w-5 h-5 text-[#d4a853]" data-id="element-44" />
                      Política de Privacidad
                    </> : <>
                      <FileTextIcon className="w-5 h-5 text-[#d4a853]" data-id="element-45" />
                      Términos y Condiciones
                    </>}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10" data-id="element-46">
                  <XIcon className="w-5 h-5" data-id="element-47" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)]" data-id="element-48">
                <div className="font-['DM_Sans'] text-[#334155] text-sm leading-relaxed whitespace-pre-line" data-id="element-49">
                  {activeModal === 'privacy' ? privacyContent : termsContent}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}